import { useState, useEffect, useCallback, useRef } from 'react';
import { useSoundNotificationsContext } from '../contexts/SoundNotificationsContext';
import { trackWebSocketConnection, getStoredUserId } from '../utils/analytics';
import { DEFAULT_COIN_ICON } from '../utils/helpers';
import { API_BASE_URL, WS_BASE_URL } from '../utils/apiConfig';

/**
 * Custom hook for WebSocket connection to trades feed
 * Based on js/tradesClient.js
 *
 * @param {Object} filters - Subscription filters
 * @param {number} filters.whaleTradesMinVolume - Minimum volume for whale trades
 * @param {number} filters.liquidationsMinVolume - Minimum volume for liquidations
 * @param {Object} options - Additional options
 * @param {boolean} options.enabled - Whether to enable WebSocket connection (default: true)
 */
export function useWebSocket(filters, options = {}) {
  const { enabled = true, playSounds = true } = options;

  // Sound notifications
  const { playTieredSound, playUnusualActivityAlert, playMillionAlert, getTier } = useSoundNotificationsContext();
  const playSoundsRef = useRef(playSounds);

  const [isConnected, setIsConnected] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState(null);
  const [latency, setLatency] = useState(null);
  const pingSentAtRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const [whaleTrades, setWhaleTrades] = useState([]);
  const [liquidations, setLiquidations] = useState([]);
  const [historicalTrades, setHistoricalTrades] = useState([]);
  const [historicalLoading, setHistoricalLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [metrics, setMetrics] = useState(null);

  // Unusual activity state
  const [unusualActivity, setUnusualActivity] = useState({
    coins: [],
    top3: [],
    timeframe: '1h',
    loading: true
  });
  const unusualActivityTimeframeRef = useRef('1h');

  // Coin-specific data state (for CoinActivityModal)
  const [coinData, setCoinData] = useState({
    whaleTrades: [],
    liquidations: [],
    hasMore: false,
    loading: false,
    loadingMore: false,
    offset: 0,
    totalCount: 0
  });
  const [coinSummary, setCoinSummary] = useState(null); // Full summary from DB
  const subscribedCoinRef = useRef(null);
  const coinDataOffsetRef = useRef(0); // Track offset without causing re-renders

  const wsRef = useRef(null);
  const symbolsCacheRef = useRef({});
  const reconnectDelayRef = useRef(1000);
  const maxReconnectDelay = 30000;
  const isConnectingRef = useRef(false);
  const shouldReconnectRef = useRef(true);
  const reconnectTimeoutRef = useRef(null);
  const filtersRef = useRef(filters);
  const enabledRef = useRef(enabled);
  const isSubscribedRef = useRef(false); // ✅ Ref to avoid closure issues

  // Load symbols cache - API first, JSON fallback
  useEffect(() => {
    const loadSymbolsCache = async () => {
      try {
        // Try API first (fresh data from MongoDB)
        const apiResponse = await fetch(`${API_BASE_URL}/api/icons/all`);
        if (apiResponse.ok) {
          const data = await apiResponse.json();
          symbolsCacheRef.current = data;
          return;
        }
      } catch (error) {
        // API failed, falling back to JSON
      }

      // Fallback to static JSON
      try {
        const response = await fetch(`${API_BASE_URL}/mapped_symbols.json`);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        symbolsCacheRef.current = data;
      } catch (error) {
        console.error('❌ [WebSocket] Error loading symbols cache:', error);
        symbolsCacheRef.current = {};
      }
    };
    loadSymbolsCache();
  }, []);

  // Normalize symbol helper (shared by sync and async versions)
  const normalizeSymbol = (symbol) =>
    symbol.toUpperCase().replace(/[-_]|SWAP|FUTURES/gi, '').replace(/USD$/i, 'USDT');

  // Sync: read icon from cache only (returns null on miss)
  const getSymbolLogoSync = useCallback((symbol) => {
    return symbolsCacheRef.current[normalizeSymbol(symbol)] || null;
  }, []);

  // Get symbol logo from cache or API
  const getSymbolLogo = useCallback(async (symbol) => {
    const normalizedSymbol = normalizeSymbol(symbol);

    // Check cache first
    if (symbolsCacheRef.current[normalizedSymbol]) {
      return symbolsCacheRef.current[normalizedSymbol];
    }

    // Fetch from API
    try {
      const response = await fetch(`${API_BASE_URL}/api/icon/${encodeURIComponent(normalizedSymbol)}`);
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.iconUrl) {
          // Store in cache for future use
          symbolsCacheRef.current[normalizedSymbol] = data.iconUrl;
          return data.iconUrl;
        }
      }
    } catch (error) {
      console.error(`❌ Error fetching icon for ${normalizedSymbol}:`, error);
    }

    // Fallback SVG
    return DEFAULT_COIN_ICON;
  }, []); // No dependencies - uses ref which doesn't trigger re-renders

  // Get exchange logo
  const getExchangeLogo = useCallback((exchange) => {
    const logos = {
      'HYPERLIQUID': '/assets_new/img/HL symbol_white.svg',
      'BINANCE': '/assets_new/img/binance.png',
      'BINANCE_FUTURES': '/assets_new/img/binance.png',
      'BYBIT': '/assets/img/bybit.svg',
      'OKX': '/assets/img/okx@3x.png',
      'COINBASE': '/assets/img/coinbase_f.png'
    };
    return logos[exchange] || '/assets_new/img/binance.png';
  }, []);

  // Subscribe to trades feed with filters
  const subscribe = useCallback((ws, filters) => {
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      return;
    }

    // Send SEPARATE thresholds for whale trades and liquidations
    // Backend will filter by trade type
    const subscriptionMessage = {
      type: 'subscribe',
      filters: {
        whaleTradesMinVolume: filters.whaleTradesMinVolume,
        liquidationsMinVolume: filters.liquidationsMinVolume,
        tradeTypes: ['whale_trade', 'liquidation']
      }
    };

    // Add selectedCoins if specified
    if (filters.selectedCoins && filters.selectedCoins.length > 0) {
      subscriptionMessage.filters.selectedCoins = filters.selectedCoins;
    }

    ws.send(JSON.stringify(subscriptionMessage));
  }, []);

  // Update filters for existing connection
  const updateFilters = useCallback((newFilters) => {
    filtersRef.current = newFilters;

    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN && isSubscribedRef.current) {
      const updateMessage = {
        type: 'update_filters',
        filters: {
          whaleTradesMinVolume: newFilters.whaleTradesMinVolume,
          liquidationsMinVolume: newFilters.liquidationsMinVolume,
          tradeTypes: ['whale_trade', 'liquidation']
        }
      };

      // Add selectedCoins if specified
      if (newFilters.selectedCoins && newFilters.selectedCoins.length > 0) {
        updateMessage.filters.selectedCoins = newFilters.selectedCoins;
      }

      wsRef.current.send(JSON.stringify(updateMessage));
    }
  }, []); // ✅ No dependencies needed - uses only refs

  // Handle whale trade — sync-first, icon backfill async
  const handleWhaleTrade = useCallback((trade) => {
    const logoUrl = getSymbolLogoSync(trade.symbol);
    const exchangeLogo = getExchangeLogo(trade.exchange);
    const baseCoin = trade.symbol.toUpperCase().replace(/USDT$|USD$/, '');

    // Get tier and play sound for trades >= $1M
    const tier = getTier(trade.dollarSize);

    // Only play sounds if playSounds option is enabled (TradingPage only)
    if (playSoundsRef.current) {
      const soundMeta = { coin: baseCoin, side: trade.side, exchange: trade.exchange };
      if (tier) {
        playTieredSound(trade.dollarSize, 'whale', soundMeta);
      }
      playMillionAlert(trade.side, trade.dollarSize, soundMeta);
    }

    const enrichedTrade = {
      ...trade,
      logoUrl,
      exchangeLogo,
      baseCoin,
      tier,
      isHistorical: false,
      timestamp: trade.timestamp || Date.now()
    };

    setWhaleTrades(prev => [enrichedTrade, ...prev].slice(0, 500));

    // Backfill icon if cache miss
    if (!logoUrl) {
      getSymbolLogo(trade.symbol).then(url => {
        if (url) {
          setWhaleTrades(prev => prev.map(t =>
            t.symbol === trade.symbol && t.timestamp === enrichedTrade.timestamp && !t.logoUrl
              ? { ...t, logoUrl: url }
              : t
          ));
        }
      });
    }
  }, [getSymbolLogoSync, getSymbolLogo, getExchangeLogo, getTier, playTieredSound, playMillionAlert]);

  // Handle liquidation — sync-first, icon backfill async
  const handleLiquidation = useCallback((trade) => {
    const logoUrl = getSymbolLogoSync(trade.symbol);
    const exchangeLogo = getExchangeLogo(trade.exchange);
    const baseCoin = trade.symbol.toUpperCase().replace(/USDT$|USD$/, '');

    let emoji = '🐟';
    if (trade.dollarSize >= 1000000) {
      emoji = '🐬';
    } else if (trade.dollarSize >= 100000) {
      emoji = '🐠';
    }

    const tier = getTier(trade.dollarSize);

    if (playSoundsRef.current && tier) {
      playTieredSound(trade.dollarSize, 'liquidation', { coin: baseCoin, side: trade.side, exchange: trade.exchange });
    }

    const enrichedTrade = {
      ...trade,
      logoUrl,
      exchangeLogo,
      baseCoin,
      emoji,
      tier,
      isHistorical: false,
      timestamp: trade.timestamp || Date.now()
    };

    setLiquidations(prev => [enrichedTrade, ...prev].slice(0, 500));

    // Backfill icon if cache miss
    if (!logoUrl) {
      getSymbolLogo(trade.symbol).then(url => {
        if (url) {
          setLiquidations(prev => prev.map(t =>
            t.symbol === trade.symbol && t.timestamp === enrichedTrade.timestamp && !t.logoUrl
              ? { ...t, logoUrl: url }
              : t
          ));
        }
      });
    }
  }, [getSymbolLogoSync, getSymbolLogo, getExchangeLogo, getTier, playTieredSound]);

  // Handle unusual activity alert
  const handleUnusualActivityAlert = useCallback((alertData) => {
    // Play sound only if playSounds is enabled (TradingPage only)
    if (playSoundsRef.current) {
      playUnusualActivityAlert();
    }

    // Dispatch custom event for UI to show banner
    window.dispatchEvent(new CustomEvent('unusualActivityAlert', {
      detail: alertData
    }));
  }, [playUnusualActivityAlert]);

  // Handle historical data received from WebSocket
  // Two-phase: sync enrichment first (instant render), then async icon backfill
  const handleHistoricalData = useCallback((trades) => {
    // Helper function to calculate tier
    const calcTier = (dollarSize) => {
      if (dollarSize < 1_000_000) return null;
      if (dollarSize < 25_000_000) return 1;
      if (dollarSize < 50_000_000) return 2;
      if (dollarSize < 100_000_000) return 3;
      return 4;
    };

    // Phase 1: sync enrichment — render trades immediately
    const missingSymbols = new Set();
    const enrichedTrades = trades.map((trade) => {
      const logoUrl = getSymbolLogoSync(trade.symbol);
      const exchangeLogo = getExchangeLogo(trade.exchange);
      const baseCoin = trade.symbol.toUpperCase().replace(/USDT$|USD$/, '');
      const tier = calcTier(trade.dollarSize);

      if (!logoUrl) missingSymbols.add(trade.symbol);

      let emoji = '🐟';
      if (trade.isLiquidation) {
        if (trade.dollarSize >= 1000000) emoji = '🐬';
        else if (trade.dollarSize >= 100000) emoji = '🐠';
      }

      return {
        ...trade,
        logoUrl,
        exchangeLogo,
        baseCoin,
        tier,
        emoji: trade.isLiquidation ? emoji : undefined,
        isHistorical: true
      };
    });

    setHistoricalTrades(enrichedTrades);
    setHistoricalLoading(false);

    // Phase 2: async icon backfill for cache misses
    if (missingSymbols.size > 0) {
      (async () => {
        const loaded = {};
        for (const symbol of missingSymbols) {
          loaded[symbol] = await getSymbolLogo(symbol);
        }
        setHistoricalTrades(prev => prev.map(t =>
          !t.logoUrl && loaded[t.symbol]
            ? { ...t, logoUrl: loaded[t.symbol] }
            : t
        ));
      })();
    }
  }, [getSymbolLogoSync, getSymbolLogo, getExchangeLogo]);

  // Handle coin data response from server
  const handleCoinDataResponse = useCallback((message) => {
    if (message.coin !== subscribedCoinRef.current) {
      // Ignore data for different coin
      return;
    }

    // Update summary if provided (first page only)
    if (message.summary) {
      setCoinSummary(message.summary);
    }

    // Update offset ref
    coinDataOffsetRef.current = message.nextOffset;

    setCoinData(prev => {
      const isInitialLoad = prev.offset === 0 && prev.loading;

      return {
        whaleTrades: isInitialLoad
          ? message.whaleTrades
          : [...prev.whaleTrades, ...message.whaleTrades],
        liquidations: isInitialLoad
          ? message.liquidations
          : [...prev.liquidations, ...message.liquidations],
        hasMore: message.hasMore,
        offset: message.nextOffset,
        totalCount: message.totalCount,
        loading: false,
        loadingMore: false
      };
    });
  }, []);

  // Handle live coin trade update
  const handleCoinTradeLive = useCallback((message) => {
    if (message.coin !== subscribedCoinRef.current) {
      // Ignore trades for different coin
      return;
    }

    // Update coin data list (cap at 500 to prevent unbounded growth)
    setCoinData(prev => {
      if (message.tradeType === 'whale_trade') {
        return {
          ...prev,
          whaleTrades: [message.trade, ...prev.whaleTrades].slice(0, 500)
        };
      } else if (message.tradeType === 'liquidation') {
        return {
          ...prev,
          liquidations: [message.trade, ...prev.liquidations].slice(0, 500)
        };
      }
      return prev;
    });

    // Update summary locally (increment counters)
    setCoinSummary(prev => {
      if (!prev) return prev;

      const dollarSize = message.trade.dollarSize || 0;

      if (message.tradeType === 'whale_trade') {
        return {
          ...prev,
          totalWhaleTrades: prev.totalWhaleTrades + 1,
          whaleVolume: prev.whaleVolume + dollarSize,
          totalVolume: prev.totalVolume + dollarSize
        };
      } else if (message.tradeType === 'liquidation') {
        return {
          ...prev,
          totalLiquidations: prev.totalLiquidations + 1,
          liqVolume: prev.liqVolume + dollarSize,
          totalVolume: prev.totalVolume + dollarSize
        };
      }

      return prev;
    });
  }, []);

  // Handle coin summary update (periodic from server)
  const handleCoinSummaryUpdate = useCallback((message) => {
    if (message.coin !== subscribedCoinRef.current) {
      // Ignore summary for different coin
      return;
    }

    setCoinSummary(message.summary);
  }, []);

  // Connect to WebSocket
  const connect = useCallback(() => {
    if (isConnectingRef.current) return;

    isConnectingRef.current = true;

    // Close previous WebSocket if still open (prevent orphaned connections)
    if (wsRef.current) {
      try { wsRef.current.onclose = null; wsRef.current.close(); } catch (e) {}
      wsRef.current = null;
    }

    // In development, connect through Vite proxy at /ws path
    const isDev = window.location.port === '5173' || window.location.port === '5174' || window.location.port === '5175';
    // SECURITY: Don't pass userId in URL - send in first message instead
    const baseUrl = isDev
      ? (WS_BASE_URL ? `${WS_BASE_URL}/ws` : `ws://localhost:${window.location.port}/ws`)
      : (WS_BASE_URL || `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}`);

    wsRef.current = new WebSocket(baseUrl);

    wsRef.current.onopen = () => {
      setIsConnected(true);
      reconnectDelayRef.current = 1000;
      isConnectingRef.current = false;
      trackWebSocketConnection('trades', 'connected');

      // SECURITY: Send userId in first message, not URL (prevents logging in proxies/history)
      const userId = getStoredUserId();
      wsRef.current.send(JSON.stringify({ type: 'identify', userId }));

      // Subscribe with filters after connection is established
      subscribe(wsRef.current, filtersRef.current);

      // Start latency ping every 10s
      if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = setInterval(() => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          pingSentAtRef.current = Date.now();
          wsRef.current.send(JSON.stringify({ type: 'ping' }));
        }
      }, 10000);
    };

    wsRef.current.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);

        switch (message.type) {
          case 'connection':
            break;

          case 'pong':
            if (pingSentAtRef.current) {
              setLatency(Date.now() - pingSentAtRef.current);
              pingSentAtRef.current = null;
            }
            break;

          case 'subscription_success':
            setIsSubscribed(true);
            isSubscribedRef.current = true; // ✅ Update ref
            setSubscriptionError(null);
            break;

          case 'subscription_error':
            console.error('❌ Subscription failed:', message.error);
            setIsSubscribed(false);
            isSubscribedRef.current = false; // ✅ Update ref
            setSubscriptionError(message.error);
            break;

          case 'filter_update_success':
            // Clear only live trades; historical data will be replaced atomically
            // when the server sends a new historical_data message (no flash)
            setWhaleTrades([]);
            setLiquidations([]);
            break;

          case 'filter_update_error':
            console.error('❌ Filter update failed:', message.error);
            break;

          case 'whale_trade':
            if (isSubscribedRef.current) {
              handleWhaleTrade(message.data);
            }
            break;

          case 'liquidation':
            if (isSubscribedRef.current) {
              handleLiquidation(message.data);
            }
            break;

          case 'historical_data':
            handleHistoricalData(message.data.trades);
            break;

          case 'summary':
            setSummary(message.data);
            break;

          case 'metrics':
            setMetrics(message.data);
            break;

          case 'unusual_activity_alert':
            handleUnusualActivityAlert(message.data);
            break;

          case 'coin_data':
            handleCoinDataResponse(message);
            break;

          case 'coin_trade':
            handleCoinTradeLive(message);
            break;

          case 'coin_summary':
            handleCoinSummaryUpdate(message);
            break;

          case 'unusual_activity_update':
            setUnusualActivity({
              coins: message.data.coins,
              top3: message.data.top3,
              timeframe: message.data.timeframe,
              loading: false
            });
            break;

          case 'unusual_activity_subscribe_success':
            break;

          case 'signal_open':
            // Cross-coin signal — re-broadcast as a window event so the
            // useCrossCoinSignals hook can subscribe without coupling to
            // this hook's internal state shape.
            try {
              const detail = message.data || {
                coin: message.coin,
                direction: message.direction,
                timeframe: message.timeframe,
                price: message.price,
                timestamp: message.timestamp,
              };
              window.dispatchEvent(new CustomEvent('signalOpen', { detail }));
            } catch (e) {
              // Ignore — malformed payloads are dropped silently.
            }
            break;

          case 'error':
            console.error('❌ Server error:', message.message);
            break;

          default:
            break;
        }
      } catch (error) {
        console.error('❌ Error parsing message:', error);
      }
    };

    wsRef.current.onerror = (error) => {
      console.error('❌ WebSocket error:', error);
      isConnectingRef.current = false;
      trackWebSocketConnection('trades', 'error');
    };

    wsRef.current.onclose = () => {
      setIsConnected(false);
      setIsSubscribed(false);
      setLatency(null);
      if (pingIntervalRef.current) { clearInterval(pingIntervalRef.current); pingIntervalRef.current = null; }
      isSubscribedRef.current = false; // ✅ Reset ref
      isConnectingRef.current = false;
      trackWebSocketConnection('trades', 'disconnected');

      // Only reconnect if we should (not unmounted)
      if (shouldReconnectRef.current) {
        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, reconnectDelayRef.current);

        reconnectDelayRef.current = Math.min(reconnectDelayRef.current * 2, maxReconnectDelay);
      }
    };
  }, [handleWhaleTrade, handleLiquidation, handleCoinDataResponse, handleCoinTradeLive, handleCoinSummaryUpdate, subscribe]);

  // Update enabled state
  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  // Update playSounds state
  useEffect(() => {
    playSoundsRef.current = playSounds;
  }, [playSounds]);

  // Update filters when they change (debounced to avoid rapid-fire updates)
  useEffect(() => {
    filtersRef.current = filters;
    const timer = setTimeout(() => {
      updateFilters(filters);
    }, 300);
    return () => clearTimeout(timer);
  }, [filters, updateFilters]);

  // Connect when enabled, disconnect when disabled
  useEffect(() => {
    if (!enabled) {
      return;
    }

    shouldReconnectRef.current = true;
    connect();

    return () => {
      // Stop reconnection attempts
      shouldReconnectRef.current = false;

      // Clear any pending reconnect timeout
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      // Close WebSocket
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]); // Connect/disconnect when enabled changes

  // Manual subscribe function for testing
  const manualSubscribe = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      subscribe(wsRef.current, filtersRef.current);
    }
  }, [subscribe]);

  // Manual unsubscribe function for testing
  const manualUnsubscribe = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'unsubscribe' }));
      setIsSubscribed(false);
      isSubscribedRef.current = false;
      setSubscriptionError(null);
    }
  }, []);

  // Simulate whale trade for testing - sends to server which broadcasts to all clients
  const simulateTrade = useCallback((trade) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      // Send to server - server will broadcast to all clients
      wsRef.current.send(JSON.stringify({
        type: 'simulate_trade',
        data: trade
      }));
    }
  }, []);

  // Simulate liquidation for testing - sends to server which broadcasts to all clients
  const simulateLiquidation = useCallback((liquidation) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      // Send to server - server will broadcast to all clients
      wsRef.current.send(JSON.stringify({
        type: 'simulate_liquidation',
        data: liquidation
      }));
    }
  }, []);

  // Request coin data with pagination
  // options: { whaleMinSize, liqMinSize } — server-side min $ size filter
  const requestCoinData = useCallback((coin, isInitialLoad = true, options = {}) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.error('❌ WebSocket not connected, cannot request coin data');
      return;
    }

    // Get offset from ref to avoid dependency on state
    const offset = isInitialLoad ? 0 : coinDataOffsetRef.current;

    if (isInitialLoad) {
      coinDataOffsetRef.current = 0;
      setCoinData({
        whaleTrades: [],
        liquidations: [],
        hasMore: false,
        loading: true,
        loadingMore: false,
        offset: 0,
        totalCount: 0
      });
    } else {
      setCoinData(prev => ({ ...prev, loadingMore: true }));
    }

    wsRef.current.send(JSON.stringify({
      type: 'request_coin_data',
      coin,
      limit: 50,
      offset,
      tradeTypes: ['whale_trade', 'liquidation'],
      whaleMinSize: options.whaleMinSize || 0,
      liqMinSize: options.liqMinSize || 0,
    }));

  }, []); // No dependencies - uses refs

  // Subscribe to coin for live updates
  const subscribeToCoin = useCallback((coin) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.error('❌ WebSocket not connected, cannot subscribe to coin');
      return;
    }

    wsRef.current.send(JSON.stringify({
      type: 'subscribe_coin',
      coin
    }));

    subscribedCoinRef.current = coin;
  }, []);

  // Unsubscribe from coin
  const unsubscribeFromCoin = useCallback((coin) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      return;
    }

    wsRef.current.send(JSON.stringify({
      type: 'unsubscribe_coin',
      coin
    }));

    subscribedCoinRef.current = null;
  }, []);

  // Clear coin data
  const clearCoinData = useCallback(() => {
    coinDataOffsetRef.current = 0;
    setCoinData({
      whaleTrades: [],
      liquidations: [],
      hasMore: false,
      loading: false,
      loadingMore: false,
      offset: 0,
      totalCount: 0
    });
    setCoinSummary(null);
    subscribedCoinRef.current = null;
  }, []);

  // Subscribe to unusual activity updates
  const subscribeToUnusualActivity = useCallback((timeframe = '1h') => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.error('❌ WebSocket not connected, cannot subscribe to unusual activity');
      return;
    }

    unusualActivityTimeframeRef.current = timeframe;
    setUnusualActivity(prev => ({ ...prev, loading: true }));

    wsRef.current.send(JSON.stringify({
      type: 'subscribe_unusual_activity',
      timeframe
    }));

  }, []);

  // Unsubscribe from unusual activity
  const unsubscribeFromUnusualActivity = useCallback(() => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      return;
    }

    wsRef.current.send(JSON.stringify({
      type: 'unsubscribe_unusual_activity'
    }));

  }, []);

  // Request unusual activity data (one-time, no subscription)
  const requestUnusualActivity = useCallback((timeframe = '1h') => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.error('❌ WebSocket not connected, cannot request unusual activity');
      return;
    }

    setUnusualActivity(prev => ({ ...prev, loading: true }));

    wsRef.current.send(JSON.stringify({
      type: 'request_unusual_activity',
      timeframe
    }));

  }, []);

  // Change unusual activity timeframe
  const changeUnusualActivityTimeframe = useCallback((timeframe) => {
    unusualActivityTimeframeRef.current = timeframe;

    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      // Unsubscribe from old timeframe and subscribe to new one
      wsRef.current.send(JSON.stringify({
        type: 'subscribe_unusual_activity',
        timeframe
      }));
    }
  }, []);

  return {
    isConnected,
    isSubscribed,
    subscriptionError,
    whaleTrades,
    liquidations,
    historicalTrades,
    historicalLoading,
    summary,
    metrics,
    subscribe: manualSubscribe,
    unsubscribe: manualUnsubscribe,
    simulateTrade,
    simulateLiquidation,
    // Coin-specific methods (for CoinActivityModal)
    coinData,
    coinSummary,
    requestCoinData,
    subscribeToCoin,
    unsubscribeFromCoin,
    clearCoinData,
    // Unusual activity methods
    unusualActivity,
    subscribeToUnusualActivity,
    unsubscribeFromUnusualActivity,
    requestUnusualActivity,
    changeUnusualActivityTimeframe,
    latency
  };
}
